import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, Search as SearchIcon, Check } from "lucide-react";

/**
 * A dropdown with a search box built into the panel itself.
 * Matching is case-insensitive substring match; results are sorted
 * alphabetically. Works for both "city" and "category" style filters —
 * pass in `options` as [{ value, label, count }].
 *
 * `clearValue` is whatever the parent treats as "nothing selected"
 * (e.g. "all", "any", or ""). Picking the pinned top row calls
 * onChange(clearValue).
 */
export default function SearchableSelect({
  icon: Icon,
  iconClassName = "text-slate-400",
  value,
  onChange,
  options,
  clearValue = "",
  clearLabel = "All",
  searchPlaceholder = "Search...",
  triggerClassName = "",
  panelClassName = "",
  emptyText = "No matches",
  "data-testid": dataTestId,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setQuery("");
      return;
    }
    const focusTimer = setTimeout(() => inputRef.current?.focus(), 10);
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(focusTimer);
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const q = query.trim().toLowerCase();
  const filtered = (q
    ? options.filter(o => o.label.toLowerCase().includes(q))
    : options
  ).slice().sort((a, b) => a.label.localeCompare(b.label));

  const selected = options.find(o => o.value === value);
  const isCleared = !value || value === clearValue;
  const displayLabel = selected ? selected.label : clearLabel;

  const pick = (v) => { onChange(v); setOpen(false); };

  return (
    <div ref={rootRef} className="relative w-full">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        data-testid={dataTestId}
        className={`flex items-center gap-2 w-full text-left ${triggerClassName}`}
      >
        {Icon && <Icon className={`w-4 h-4 shrink-0 ${iconClassName}`} />}
        <span className={`truncate flex-1 text-sm ${isCleared ? "text-slate-500" : "text-slate-800"}`}>
          {displayLabel}
        </span>
        <ChevronDown className={`w-3.5 h-3.5 text-slate-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          className={`absolute z-50 mt-1 w-full min-w-[220px] bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden ${panelClassName}`}
        >
          <div className="p-2 border-b border-slate-100 relative">
            <SearchIcon className="absolute left-5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-black pointer-events-none" />
            <input
              ref={inputRef}
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full pl-7 pr-2 py-1.5 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-1 focus:ring-black"
              data-testid={dataTestId ? `${dataTestId}-search` : undefined}
            />
          </div>
          <div className="max-h-64 overflow-y-auto py-1">
            <button
              type="button"
              onClick={() => pick(clearValue)}
              className={`flex items-center w-full px-3 py-2 text-sm text-left hover:bg-slate-50 ${isCleared ? "font-semibold text-black" : "text-slate-600"}`}
            >
              {isCleared && <Check className="w-3.5 h-3.5 mr-1.5 shrink-0" />}
              {clearLabel}
            </button>
            {filtered.length === 0 ? (
              <p className="px-3 py-3 text-xs text-slate-400">{emptyText}</p>
            ) : filtered.map(o => (
              <button
                key={o.value}
                type="button"
                onClick={() => pick(o.value)}
                className={`flex items-center w-full px-3 py-2 text-sm text-left hover:bg-slate-50 ${value === o.value ? "font-semibold text-black bg-slate-50" : "text-slate-700"}`}
              >
                <span className="truncate flex-1">{o.label}</span>
                {o.count > 0 && <span className="ml-auto pl-2 text-xs text-slate-400 shrink-0">{o.count}</span>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
